import {
  Avatar,
  Box,
  Button,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import React from "react";
import { useUser } from "../contexts/useUser";
import { useQuery } from "@tanstack/react-query";
import { fetchLLMCustomers } from "../dataaccess/fetchLLMCustomers";
import { useNavigate, useSearchParams } from "react-router-dom";
import MemoryAndKnowledgeGraph from "./MemoryAndKnowledgeGraph";

const Customers: React.FC = () => {
  const { project } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerIdFromUrl = searchParams.get("customerId");

  const [nameFilter, setNameFilter] = React.useState<string | undefined>(
    undefined
  );
  const [tokensFilter, setTokensFilter] = React.useState<number | undefined>(
    undefined
  );
  const [selectedCustomer, setSelectedCustomer] = React.useState<any | null>(
    null
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ["customers"],
    queryFn: () => fetchLLMCustomers(project?.id ?? ""),
    enabled: project?.id && project?.id.length > 0 ? true : false,
  });

  // Auto-select customer from URL parameter
  React.useEffect(() => {
    if (
      customerIdFromUrl &&
      data?.data?.customersByProjectIdAndLastCallMadeAt?.items
    ) {
      const customer = data.data.customersByProjectIdAndLastCallMadeAt.items.find(
        (c: any) => c.id === customerIdFromUrl
      );
      if (customer) {
        setSelectedCustomer(customer);
      }
    }
  }, [customerIdFromUrl, data]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error...</div>;
  }

  const customers =
    data?.data?.customersByProjectIdAndLastCallMadeAt?.items ?? [];

  return (
    <>
      <Box p={2}>
        <Grid container spacing={2} sx={{ height: "calc(100vh - 100px)" }}>
          {/* Left Side - Customer List */}
          <Grid
            item
            xs={6}
            sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            {/* Enhanced Filter Panel */}
            <Paper
              elevation={3}
              style={{
                borderRadius: 3,
                height: 120,
                border: "1px solid #444",
                marginBottom: 16,
              }}
            >
              <Box sx={{ p: 2, height: "100%" }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <FilterListIcon fontSize="small" />
                  Customer Filters
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={5}>
                    <TextField
                      variant="outlined"
                      onChange={(e) => setNameFilter(e.currentTarget.value)}
                      value={nameFilter}
                      size="small"
                      fullWidth
                      placeholder="Search customers"
                      InputProps={{
                        startAdornment: (
                          <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Tokens less than"
                      variant="outlined"
                      value={tokensFilter?.toString()}
                      onChange={(e) =>
                        setTokensFilter(
                          e.currentTarget.value
                            ? parseInt(e.currentTarget.value)
                            : undefined
                        )
                      }
                      size="small"
                      type="number"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={() => {
                        setNameFilter("");
                        setTokensFilter(undefined);
                      }}
                    >
                      Clear
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* Enhanced Customer List */}
            <Paper
              elevation={3}
              style={{ borderRadius: 3, border: "1px solid #444", flex: 1 }}
            >
              <Box height="100%" sx={{ overflow: "auto" }}>
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h6">
                    Customers (
                    {
                      customers.filter((customer) => {
                        if (
                          nameFilter &&
                          customer?.name
                            ?.toLowerCase()
                            .indexOf(nameFilter.toLowerCase()) === -1
                        )
                          return false;
                        if (
                          tokensFilter &&
                          (customer?.tokensRemaining ?? 0) >= tokensFilter
                        )
                          return false;
                        return true;
                      }).length
                    }
                    )
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => navigate("new")}
                  >
                    New Customer
                  </Button>
                </Box>

                <List sx={{ p: 0 }}>
                  {customers
                    .filter((customer) => {
                      if (
                        nameFilter &&
                        customer?.name
                          ?.toLowerCase()
                          .indexOf(nameFilter.toLowerCase()) === -1
                      ) {
                        return false;
                      }

                      if (
                        tokensFilter &&
                        (customer?.tokensRemaining ?? 0) >= tokensFilter
                      ) {
                        return false;
                      }

                      return true;
                    })
                    .map((customer) => (
                      <ListItem
                        key={customer?.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          bgcolor:
                            selectedCustomer?.id === customer?.id
                              ? "action.selected"
                              : "transparent",
                          "&:hover": {
                            bgcolor: "action.hover",
                          },
                          pr: 1,
                        }}
                        onClick={() => setSelectedCustomer(customer)}
                      >
                        <ListItemAvatar sx={{ minWidth: 56 }}>
                          <Avatar
                            sx={{
                              bgcolor:
                                customer?.status === "active"
                                  ? "primary.main"
                                  : "secondary.main",
                            }}
                          >
                            {customer?.numberOfCallsMade ?? 0}
                          </Avatar>
                        </ListItemAvatar>

                        <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {customer?.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            status: {customer?.status}, last call made:{" "}
                            {customer?.lastCallMadeAt
                              ? new Date(
                                  customer?.lastCallMadeAt
                                ).toLocaleString()
                              : "no calls yet"}
                            , tokens remaining: {customer?.tokensRemaining ?? 0}{" "}
                            (last replenished:{" "}
                            {customer?.lastReplenishedAt
                              ? new Date(
                                  customer?.lastReplenishedAt
                                ).toLocaleString()
                              : "never"}
                            )
                          </Typography>
                        </Box>

                        <Box
                          sx={{ flexShrink: 0, ml: 1, display: "flex", gap: 1 }}
                        >
                          <Button
                            variant="text"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`edit/${customer?.id}`);
                            }}
                          >
                            Edit
                          </Button>
                        </Box>
                      </ListItem>
                    ))}
                </List>
              </Box>
            </Paper>
          </Grid>

          {/* Right Side - Memory and Knowledge Graph */}
          <Grid item xs={6} sx={{ height: "100%" }}>
            <Paper
              elevation={3}
              style={{
                borderRadius: 3,
                border: "1px solid #444",
                height: "100%",
              }}
            >
              <MemoryAndKnowledgeGraph
                selectedCustomer={selectedCustomer}
              />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default Customers;
